import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  closestCorners, useDroppable
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Plus, AlertTriangle, Calendar, FileText, GripVertical, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';
import { getCompanyFilterLabel } from '../lib/serviceGroups';

const PHASES = [
  { id: 1, label: 'Phase 1', sublabel: 'Erfassung', color: 'bg-slate-100 border-slate-200 dark:bg-slate-800/50 dark:border-slate-700' },
  { id: 2, label: 'Phase 2', sublabel: 'Terminierung', color: 'bg-blue-50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800/50' },
  { id: 3, label: 'Phase 3', sublabel: 'Auswertung', color: 'bg-amber-50 border-amber-100 dark:bg-amber-900/20 dark:border-amber-800/50' },
  { id: 4, label: 'Phase 4', sublabel: 'Entscheid', color: 'bg-purple-50 border-purple-100 dark:bg-purple-900/20 dark:border-purple-800/50' },
];
const PHASE_BADGE = ['', 'phase-1', 'phase-2', 'phase-3', 'phase-4'];

const STATUS_BADGES = {
  open: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  accepted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const STATUS_LABELS = { open: 'Offen', accepted: 'Angenommen', rejected: 'Abgelehnt' };

const getDeadlineBadge = (dateValue) => {
  if (!dateValue) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const daysUntil = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntil <= 0) {
    return {
      label: `${Math.abs(daysUntil)}T`,
      title: 'Tage seit Deadline',
      tone: 'red',
      color: 'bg-red-100 border-red-400 text-red-700',
      border: 'border-red-400',
    };
  }
  if (daysUntil <= 3) {
    return {
      label: `${daysUntil}T`,
      title: 'Tage bis Deadline',
      tone: 'orange',
      color: 'bg-amber-100 border-amber-400 text-amber-700',
      border: 'border-amber-400',
    };
  }
  return null;
};

const QuoteCard = ({ quote, isDragging = false }) => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const isOverdue = quote.deadline && quote.deadline <= today;
  const deadlineBadge = getDeadlineBadge(quote.deadline);
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: quote.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`quote-card-${quote.id}`}
      onClick={() => navigate(`/quotes/${quote.id}`)}
      className={cn(
        "bg-card border rounded-sm p-4 cursor-pointer hover:shadow-sm hover:border-primary/30 transition-all",
        deadlineBadge ? `border-2 ${deadlineBadge.border}` : "border-border/60"
      )}
    >
      <div className="flex items-start gap-2 mb-2">
        <div {...attributes} {...listeners} className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground" onClick={e => e.stopPropagation()}>
          <GripVertical className="w-3.5 h-3.5" strokeWidth={1.5} />
        </div>
        <div className="flex items-start justify-between flex-1 min-w-0">
          <span className="text-xs font-mono text-muted-foreground">{quote.quote_number}</span>
          <div className="flex items-center gap-1">
            {deadlineBadge && (
              <span className={cn("text-xs font-bold px-1.5 py-0.5 rounded border", deadlineBadge.color)} title={deadlineBadge.title}>
                {deadlineBadge.label}
              </span>
            )}
            {deadlineBadge && (
              <AlertTriangle
                className={cn("w-3.5 h-3.5 shrink-0", deadlineBadge.tone === 'red' ? 'text-destructive' : 'text-amber-600')}
                strokeWidth={2}
              />
            )}
            <span className={cn("text-xs font-medium px-2 py-0.5 rounded-sm", STATUS_BADGES[quote.status])}>
              {STATUS_LABELS[quote.status]}
            </span>
          </div>
        </div>
      </div>
      <p className="text-sm font-semibold text-foreground line-clamp-2 mb-2">{quote.title}</p>
      {quote.customer_name && <p className="text-xs text-muted-foreground truncate">{quote.customer_name}</p>}
      {getCompanyFilterLabel(quote) && <p className="text-xs text-muted-foreground truncate">{getCompanyFilterLabel(quote)}</p>}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/40">
        {quote.deadline ? (
          <div className={cn("flex items-center gap-1 text-xs", isOverdue ? "text-destructive" : deadlineBadge ? "text-amber-600" : "text-muted-foreground")}>
            <Calendar className="w-3 h-3" strokeWidth={1.5} />
            <span className="font-mono">{quote.deadline}</span>
          </div>
        ) : <span />}
        {quote.totals?.total > 0 && (
          <span className="text-xs font-mono font-medium">CHF {quote.totals.total.toFixed(2)}</span>
        )}
      </div>
    </div>
  );
};

const PhaseColumn = ({ phase, quotes, isOver }) => {
  const { setNodeRef } = useDroppable({ id: `phase-${phase.id}` });
  return (
    <div ref={setNodeRef} className={cn("w-72 shrink-0 border rounded-sm p-3 transition-colors", phase.color, isOver ? "ring-2 ring-primary ring-offset-1" : "")}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{phase.label}</p>
          <p className="text-sm font-medium text-foreground">{phase.sublabel}</p>
        </div>
        <span className={cn("text-xs font-mono font-bold px-2 py-0.5 rounded-sm", PHASE_BADGE[phase.id])}>{quotes.length}</span>
      </div>
      <div className="space-y-2 min-h-[120px] max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
        {quotes.length === 0 ? (
          <div className={cn("py-8 text-center rounded-sm border-2 border-dashed", isOver ? "border-primary/50 bg-primary/5" : "border-transparent")}>
            <p className="text-xs text-muted-foreground">{isOver ? "Hier ablegen" : "Keine Offerten"}</p>
          </div>
        ) : quotes.map(q => <QuoteCard key={q.id} quote={q} />)}
      </div>
    </div>
  );
};

export default function QuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    api.get('/quotes')
      .then(res => setQuotes(res.data))
      .catch(() => toast.error('Offerten konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  const companies = Array.from(new Set(quotes.map(getCompanyFilterLabel).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'de-CH'));
  const customers = Array.from(new Set(quotes.map(q => (q.customer_name || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'de-CH'));
  const normalizedSearch = search.trim().toLowerCase();
  const visibleQuotes = quotes.filter(q => {
    if (companyFilter !== 'all' && getCompanyFilterLabel(q) !== companyFilter) return false;
    if (customerFilter !== 'all' && (q.customer_name || '').trim() !== customerFilter) return false;
    if (!normalizedSearch) return true;
    return [q.quote_number, q.title, q.customer_name, q.property_address, q.renter_name, q.monteur_company, q.monteur_name, getCompanyFilterLabel(q)]
      .some(v => (v || '').toString().toLowerCase().includes(normalizedSearch));
  });
  const quotesByPhase = (phase) => visibleQuotes.filter(q => q.phase === phase);
  const activeQuote = activeId ? quotes.find(q => q.id === activeId) : null;

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    setOverId(null);
    if (!over) return;
    const newPhase = parseInt(over.id.toString().replace('phase-', ''));
    const quote = quotes.find(q => q.id === active.id);
    if (!quote || quote.phase === newPhase || isNaN(newPhase)) return;
    setQuotes(prev => prev.map(q => q.id === active.id ? { ...q, phase: newPhase } : q));
    try {
      await api.patch(`/quotes/${active.id}/phase`, { phase: newPhase });
      toast.success(`Verschoben zu Phase ${newPhase}`);
    } catch {
      toast.error('Verschieben fehlgeschlagen');
      api.get('/quotes').then(res => setQuotes(res.data));
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="quotes-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl text-foreground">Offerten Phasenansicht</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{visibleQuotes.length} von {quotes.length} Offerten</p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="rounded-sm gap-2">
              <Link to="/quotes">
                <FileText className="w-4 h-4" />
                Übersicht
              </Link>
            </Button>
            {hasPermission('can_create') && (
              <Button onClick={() => navigate('/quotes/new')} data-testid="new-quote-btn" className="rounded-sm gap-2">
                <Plus className="w-4 h-4" />
                Neue Offerte
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,1fr)_220px_220px] gap-3 mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Offerten suchen..." className="pl-9 rounded-sm" data-testid="quote-search" />
          </div>
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="rounded-sm" data-testid="quote-company-filter"><SelectValue placeholder="Firma" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen</SelectItem>
              {companies.map(company => <SelectItem key={company} value={company}>{company}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="rounded-sm" data-testid="quote-customer-filter"><SelectValue placeholder="Verwaltung" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Verwaltungen</SelectItem>
              {customers.map(customer => <SelectItem key={customer} value={customer}>{customer}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={({ active }) => setActiveId(active.id)} onDragOver={({ over }) => setOverId(over?.id || null)} onDragEnd={handleDragEnd}>
            <div className="overflow-x-auto -mx-6 px-6">
              <div className="flex gap-4 min-w-max pb-4">
                {PHASES.map(phase => <PhaseColumn key={phase.id} phase={phase} quotes={quotesByPhase(phase.id)} isOver={overId === `phase-${phase.id}`} />)}
              </div>
            </div>
            <DragOverlay>
              {activeQuote ? (
                <div className="bg-card border border-primary rounded-sm p-4 shadow-xl w-72 opacity-95 rotate-1">
                  <span className="text-xs font-mono text-muted-foreground">{activeQuote.quote_number}</span>
                  <p className="text-sm font-semibold mt-1">{activeQuote.title}</p>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </Layout>
  );
}
